/* BulkOpsDialog — combineert bulk-delete met cascade-preview én bulk-update
   voor Kinderen / Ouders / Medewerkers. Aangeroepen vanuit een toolbar boven
   de DataTable van de betreffende pagina.

   Mode "delete": haalt POST /{resource}/bulk-impact op, toont per-record breakdown,
   verplicht reden + cascade toggle.
   Mode "update": toont formulier met de mee-te-geven velden (fields prop) en post
   naar POST /{resource}/bulk-update. */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const RESOURCE_LABEL = {
  children: { single: "kind", plural: "kinderen" },
  parents: { single: "ouder", plural: "ouders" },
  medewerkers: { single: "medewerker", plural: "medewerkers" },
};

const IMPACT_KEYS = {
  children: [
    ["contracten", "Contracten"],
    ["facturen", "Facturen"],
    ["presentie", "Presentie"],
    ["schriftje", "Schriftje"],
    ["daglog", "Daglog"],
    ["mentorbericht", "Mentorberichten"],
  ],
  parents: [
    ["kinderen", "Kinderen"],
    ["facturen", "Facturen"],
    ["openstaande_facturen", "Openstaand"],
    ["contracten", "Contracten"],
  ],
  medewerkers: [
    ["mentor_van_kinderen", "Mentor van kinderen"],
    ["user_accounts", "User accounts"],
    ["rooster_shifts", "Rooster-shifts"],
  ],
};

export default function BulkOpsDialog({
  open, onClose, mode, resource, ids, updateFields, onDone,
}) {
  /* mode: "delete" | "update"
     resource: "children" | "parents" | "medewerkers"
     ids: string[]
     updateFields: rendered form fields (only for mode=update) — see specs below */
  const [impact, setImpact] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [values, setValues] = useState({});
  const label = RESOURCE_LABEL[resource] || { single: resource, plural: resource };

  useEffect(() => {
    if (!open) return;
    setReason("");
    setValues({});
    if (mode === "delete") {
      setImpact(null);
      api.post(`/${resource}/bulk-impact`, { ids })
        .then(r => setImpact(r.data))
        .catch(e => toast.error(formatApiError(e.response?.data?.detail)));
    }
  }, [open, mode, resource, ids]);

  const doDelete = async (cascade) => {
    if (!reason.trim()) {
      toast.error("Reden verplicht voor audit-log");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/${resource}/bulk-delete`, {
        ids, reason: reason.trim(), cascade,
      });
      const skipped = r.data.skipped?.length || 0;
      toast.success(
        `${r.data.deleted} ${label.plural} verwijderd` +
        (skipped ? ` · ${skipped} overgeslagen (historie)` : "")
      );
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doUpdate = async () => {
    // Filter: alleen expliciet ingestelde velden meegeven.
    // Speciale sentinel "__null__" → verzendt letterlijk lege string / null zodat backend
    // (bijv. mentor-koppeling) kan worden leeg gemaakt.
    const payload = { ids, reason: reason.trim() || null };
    let hasField = false;
    Object.entries(values).forEach(([k, v]) => {
      if (v === undefined) return;
      if (v === "__null__") { payload[k] = ""; hasField = true; return; }
      if (v === null || v === "") return;
      payload[k] = v;
      hasField = true;
    });
    if (!hasField) {
      toast.error("Geef minstens één veld op om te wijzigen");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/${resource}/bulk-update`, payload);
      toast.success(`${r.data.updated} ${label.plural} bijgewerkt`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const isDelete = mode === "delete";
  const impactKeys = IMPACT_KEYS[resource] || [];
  const totalImpact = impact?.grand_total || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl" data-testid={`bulk-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDelete ? "text-red-700" : "text-neutral-800"}`}>
            {isDelete ? <AlertTriangle className="h-5 w-5" /> : <Wand2 className="h-5 w-5" />}
            {isDelete
              ? `Verwijder ${ids.length} ${ids.length === 1 ? label.single : label.plural}`
              : `Wijzig ${ids.length} ${ids.length === 1 ? label.single : label.plural}`}
          </DialogTitle>
          <DialogDescription>
            {isDelete
              ? "Permanent en onomkeerbaar — wordt vastgelegd in het audit-logboek."
              : "Alleen ingevulde velden worden bijgewerkt. Niet-ingevulde velden blijven ongewijzigd."}
          </DialogDescription>
        </DialogHeader>

        {isDelete && !impact && (
          <div className="text-sm text-neutral-500 py-6 text-center">Impact laden…</div>
        )}

        {isDelete && impact && (
          <div className="space-y-3">
            {totalImpact > 0 ? (
              <div className="p-3 rounded-md border-2 border-amber-300 bg-amber-50" data-testid="bulk-impact">
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-widest mb-2">
                  Totaal gekoppelde data ({impact.found} van {impact.requested} {label.plural})
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                  {impactKeys.map(([k, lab]) => (
                    impact.totaal?.[k] != null && (
                      <div key={k} className="flex justify-between">
                        <span className="text-neutral-700">{lab}</span>
                        <span className="font-mono font-semibold text-amber-900" data-testid={`bulk-impact-${k}`}>
                          {impact.totaal[k]}
                        </span>
                      </div>
                    )
                  ))}
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 text-sm text-emerald-800">
                Geen gekoppelde data — veilig te verwijderen.
              </div>
            )}
            {impact.missing_or_out_of_scope?.length > 0 && (
              <div className="text-[11px] text-neutral-500">
                {impact.missing_or_out_of_scope.length} record(s) buiten uw scope of niet gevonden — worden overgeslagen.
              </div>
            )}
            {impact.per_record?.length > 0 && totalImpact > 0 && (
              <details className="border rounded-md" data-testid="bulk-impact-per-record">
                <summary className="px-3 py-2 text-xs cursor-pointer text-neutral-600 hover:bg-neutral-50">
                  Per-record breakdown ({impact.per_record.length})
                </summary>
                <div className="max-h-40 overflow-y-auto text-xs divide-y divide-neutral-100">
                  {impact.per_record.map(r => (
                    <div key={r.id} className="px-3 py-1.5 flex justify-between">
                      <span>{r.naam}</span>
                      <span className="text-neutral-500">{r.totaal} gekoppeld</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            <div>
              <Label htmlFor="bulk-reason">Reden (verplicht — audit-log)</Label>
              <Input
                id="bulk-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={`Bijv. inschrijving beëindigd per ${new Date().toLocaleDateString('nl-NL')}`}
                data-testid="bulk-reason"
                className="mt-1"
              />
            </div>
          </div>
        )}

        {!isDelete && (
          <div className="space-y-3">
            {updateFields?.map(f => (
              <div key={f.key}>
                <Label>{f.label} {f.hint && <span className="text-[11px] text-neutral-500 ml-1">{f.hint}</span>}</Label>
                {f.type === "select" && (
                  <Select value={values[f.key] ?? ""} onValueChange={v => setValues({ ...values, [f.key]: v })}>
                    <SelectTrigger data-testid={`bulk-field-${f.key}`}><SelectValue placeholder={f.placeholder || "— niet wijzigen —"} /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {f.allowClear && <SelectItem value="__null__">— leegmaken —</SelectItem>}
                      {f.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {f.type === "text" && (
                  <Input
                    value={values[f.key] ?? ""}
                    onChange={e => setValues({ ...values, [f.key]: e.target.value })}
                    placeholder={f.placeholder || ""}
                    data-testid={`bulk-field-${f.key}`}
                  />
                )}
                {f.type === "boolean" && (
                  <div className="flex gap-2 mt-1">
                    <Button size="sm" variant={values[f.key] === true ? "default" : "outline"}
                            onClick={() => setValues({ ...values, [f.key]: true })}
                            data-testid={`bulk-field-${f.key}-true`}>
                      {f.trueLabel || "Aan"}
                    </Button>
                    <Button size="sm" variant={values[f.key] === false ? "default" : "outline"}
                            onClick={() => setValues({ ...values, [f.key]: false })}
                            data-testid={`bulk-field-${f.key}-false`}>
                      {f.falseLabel || "Uit"}
                    </Button>
                    {values[f.key] !== undefined && (
                      <Button size="sm" variant="ghost" onClick={() => setValues({ ...values, [f.key]: undefined })}>
                        wissen
                      </Button>
                    )}
                  </div>
                )}
                {f.type === "multi" && (
                  <div className="mt-1 max-h-40 overflow-y-auto border rounded p-2 space-y-1">
                    {f.options.map(o => {
                      const arr = values[f.key] || [];
                      const on = arr.includes(o.value);
                      return (
                        <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={on}
                            onCheckedChange={(c) => {
                              const cur = new Set(arr);
                              if (c) cur.add(o.value); else cur.delete(o.value);
                              setValues({ ...values, [f.key]: [...cur] });
                            }}
                            data-testid={`bulk-field-${f.key}-${o.value}`}
                          />
                          {o.label}
                        </label>
                      );
                    })}
                    {(values[f.key] !== undefined) && (
                      <Button size="sm" variant="ghost" onClick={() => setValues({ ...values, [f.key]: undefined })}
                              className="text-[11px] mt-1">wissen (niet wijzigen)</Button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div>
              <Label htmlFor="bulk-reason">Reden <span className="text-[11px] text-neutral-500">(optioneel)</span></Label>
              <Input
                id="bulk-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Bijv. groepsverplaatsing 1 september"
                data-testid="bulk-reason"
                className="mt-1"
              />
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuleren</Button>
          {isDelete && impact && (
            totalImpact > 0 ? (
              <Button
                onClick={() => doDelete(true)}
                disabled={busy || !reason.trim()}
                data-testid="bulk-delete-cascade"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Cascade verwijderen ({totalImpact} gekoppeld)
              </Button>
            ) : (
              <Button
                onClick={() => doDelete(false)}
                disabled={busy || !reason.trim()}
                data-testid="bulk-delete-safe"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Verwijderen
              </Button>
            )
          )}
          {!isDelete && (
            <Button
              onClick={doUpdate}
              disabled={busy}
              data-testid="bulk-update-save"
              style={{ background: "var(--sage)" }} className="text-white"
            >
              <Wand2 className="h-4 w-4 mr-1.5" />
              Toepassen op {ids.length}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
